import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Breadcrumb from "./Breadcrumb";
import SEO from "./SEO";
import {
  buildKeywordMap,
  injectInternalLinks,
  slugToTitle,
  createSharedUsedSet,
} from "../utils/keywordToLinkMapper";
import { hasItinerary } from "../utils/contentAvailability";

const API = process.env.REACT_APP_API_URL;

/* ─── Derive city slug from blog slug ─── */
const deriveCitySlug = (slug = "") =>
  slug
    .replace(/-travel-guide$/, "")
    .replace(/-guide$/, "")
    .replace(/-tips$/, "")
    .replace(/-itinerary$/, "");

/* ─── Parse raw content into typed block array ─── */
const parseContent = (raw = "") =>
  raw
    .split("\n\n")
    .map((block, i) => {
      const t = block.trim();
      if (!t) return null;
      if (t.startsWith("img:")) return { type: "image", src: t.replace("img:", "").trim(), key: i };
      if (t.startsWith("## ")) return { type: "h2", text: t.replace("## ", "").trim(), key: i };
      if (t.startsWith("# "))  return { type: "h1", text: t.replace("# ", "").trim(),  key: i };
      return { type: "paragraph", text: t, key: i };
    })
    .filter(Boolean);

/* ─── Single content block renderer ─── */
/* Headings are intentionally NOT auto-linked (SEO best practice) */
const ContentBlock = ({ block, keywords, usedSet }) => {
  switch (block.type) {
    case "h1":
      return (
        <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 leading-tight mt-10 mb-2">
          {block.text}
        </h1>
      );
    case "h2":
      return (
        <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-2 border-l-4 border-[#5b6f00] pl-4 leading-snug">
          {block.text}
        </h2>
      );
    case "image":
      return (
        <figure className="my-8">
          <img
            src={block.src}
            alt="Travel guide content"
            loading="lazy"
            className="w-full rounded-2xl object-cover max-h-[480px] shadow-md"
          />
        </figure>
      );
    default:
      /* paragraphs get auto-linked; max 2 links per paragraph */
      return (
        <p className="text-[18px] leading-[1.85] text-gray-700 tracking-[-0.01em]">
          {injectInternalLinks(block.text, keywords, usedSet, 2)}
        </p>
      );
  }
};



/* ─── Skeleton loader ─── */
const ReadingSkeleton = () => (
  <section className="bg-[#fdfcf7] min-h-screen px-4 pt-28 pb-28 animate-pulse">
    <div className="max-w-[700px] mx-auto space-y-8">
      <div className="h-4 bg-gray-200 rounded w-32" />
      <div className="space-y-3">
        <div className="h-8 bg-gray-200 rounded w-3/4" />
        <div className="h-8 bg-gray-200 rounded w-1/2" />
        <div className="h-3 bg-gray-200 rounded w-40 mt-2" />
      </div>
      <div className="h-[380px] bg-gray-200 rounded-2xl w-full" />
      <div className="space-y-4">
        {[100, 95, 90, 80, 100, 70].map((w, i) => (
          <div key={i} className="h-4 bg-gray-200 rounded" style={{ width: `${w}%` }} />
        ))}
      </div>
    </div>
  </section>
);

/* ══════════════════════════════════════════════════ */

const BlogDetail = () => {
  const { slug } = useParams();

  const [blog, setBlog]               = useState(null);
  const [loading, setLoading]         = useState(true);
  const [relatedBlogs, setRelatedBlogs]         = useState([]);
  // const [relatedBlogs, setRelatedBlogs]         = useState([]);

  /* Derived slugs */
  const citySlug = useMemo(() => deriveCitySlug(slug || ""), [slug]);
  const cityName = useMemo(() => slugToTitle(citySlug), [citySlug]);

  /* ── Memoized keyword map (no recompute on unrelated renders) ── */
  const keywords = useMemo(
    () => buildKeywordMap([citySlug], [citySlug]),
    [citySlug]
  );

  /* ── Fetch current blog ── */
  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    fetch(`${API}/api/blogs/${slug}`)
      .then((res) => { if (!res.ok) throw new Error(); return res.json(); })
      .then((data) => { setBlog(data); setLoading(false); })
      .catch(() => { setBlog(null); setLoading(false); });
  }, [slug]);

  /* ── Fetch related blogs + related itineraries ── */
  useEffect(() => {
    if (!API) return;
    fetch(`${API}/api/blogs`)
      .then((r) => r.json())
      .then((data) => {
        if (!Array.isArray(data)) return;
        /* Score by keyword overlap with current slug */
        const scored = data
          .filter((b) => b.slug !== slug)
          .map((b) => ({
            ...b,
            score:
              (b.slug?.includes(citySlug) ? 2 : 0) +
              (b.title?.toLowerCase().includes(cityName.toLowerCase()) ? 1 : 0),
          }))
          .sort((a, b) => b.score - a.score)
          .slice(0, 3);
        setRelatedBlogs(scored);
      })
      .catch(() => setRelatedBlogs([]));
  }, [slug, citySlug, cityName]);



  if (loading) return <ReadingSkeleton />;

  if (!blog) {
    return (
      <>
        <Helmet>
          <meta name="robots" content="noindex, follow" />
        </Helmet>
        <section className="bg-[#fdfcf7] min-h-screen flex flex-col items-center justify-center gap-5 px-4">
          <span className="text-5xl opacity-30">🔍</span>
          <h2 className="text-xl font-semibold text-gray-700">Article not found</h2>
          <p className="text-sm text-gray-400">This article may have been moved or removed.</p>
          <Link
            to="/blogs"
            className="mt-1 text-sm font-semibold text-[#5b6f00] hover:underline underline-offset-2 transition"
          >
            ← Back to all articles
          </Link>
        </section>
      </>
    );
  }

  const formattedDate = blog.publishedAt
    ? new Date(blog.publishedAt).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : "";

  /* Shared usedSet — created fresh per render so links reset on slug change */
  const usedSet = createSharedUsedSet();
  const blocks  = parseContent(blog.content);

  /* ── JSON-LD: BlogPosting schema ── */
  const blogSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: blog.title,
    image: blog.cover,
    description: blog.excerpt,
    url: `https://www.expeditio.world/blogs/${blog.slug}`,
    datePublished: blog.publishedAt,
    dateModified: blog.updatedAt || blog.publishedAt,
    author: { "@type": "Organization", name: "Expeditio", url: "https://www.expeditio.world" },
    publisher: {
      "@type": "Organization",
      name: "Expeditio",
      logo: { "@type": "ImageObject", url: "https://www.expeditio.world/logo.png" },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": `https://www.expeditio.world/blogs/${blog.slug}` },
  };

  /* ── JSON-LD: BreadcrumbList ── */
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://www.expeditio.world/" },
      { "@type": "ListItem", position: 2, name: "Blogs", item: "https://www.expeditio.world/blogs" },
      { "@type": "ListItem", position: 3, name: blog.title, item: `https://www.expeditio.world/blogs/${blog.slug}` },
    ],
  };

  return (
    <>
      <SEO 
        title={blog.title}
        description={blog.excerpt}
        url={`/blogs/${blog.slug}`}
        image={blog.cover}
        type="article"
      />
      <Helmet>
        <meta name="keywords" content={`${cityName} travel, ${cityName} guide, ${blog.title}`} />
        <script type="application/ld+json">{JSON.stringify(blogSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <section className="bg-[#fdfcf7] min-h-screen px-4 pt-28 pb-28">
        <article className="max-w-[700px] mx-auto">

          {/* ── BREADCRUMB ── */}
          <Breadcrumb crumbs={[
            { label: "Home", to: "/" },
            { label: "Blogs", to: "/blogs" },
            { label: blog.title },
          ]} />

          {/* ── HEADER ── */}
          <header className="space-y-5 mb-10">
            <div className="flex items-center gap-2 text-xs text-gray-400 font-medium tracking-wide uppercase">
              {formattedDate && <span>{formattedDate}</span>}
              {blog.readTime && (
                <>
                  <span className="w-1 h-1 rounded-full bg-gray-300 inline-block" />
                  <span>{blog.readTime}</span>
                </>
              )}
            </div>
            <h1 className="text-3xl md:text-[2.6rem] font-extrabold text-gray-900 leading-tight tracking-tight">
              {blog.title}
            </h1>
            {blog.excerpt && (
              <p className="text-lg text-gray-500 leading-relaxed border-l-4 border-[#5b6f00]/30 pl-4">
                {blog.excerpt}
              </p>
            )}
            <div className="border-t border-gray-200 pt-2" />
          </header>

          {/* ── HERO IMAGE ── */}
          {blog.cover && (
            <figure className="mb-12">
              <img
                src={blog.cover}
                alt={blog.title}
                className="w-full rounded-2xl object-cover max-h-[420px] shadow-md"
              />
            </figure>
          )}

          {/* ── CONTENT (auto-linked paragraphs, headings skipped) ── */}
          <div className="space-y-6">
            {blocks.map((block) => (
              <ContentBlock
                key={block.key}
                block={block}
                keywords={keywords}
                usedSet={usedSet}
              />
            ))}
          </div>

          {/* ── TOPIC CLUSTER SECTION (SEO Authority) ── */}
          <aside className="mt-16 pt-10 border-t border-gray-200">
            <div className="bg-[#f4f5f0] rounded-[32px] p-8 border border-[#e8ebd9]">
              <h2 className="text-xl font-bold text-gray-900 mb-6">More about {cityName}</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <Link
                  to={`/cities/${citySlug}`}
                  className="flex flex-col bg-white rounded-2xl p-5 hover:shadow-md transition-shadow border border-transparent hover:border-[#5b7c67]/30"
                >
                  <span className="text-[10px] uppercase tracking-widest text-[#5b7c67] mb-2 font-semibold">City Guide</span>
                  <span className="text-sm font-semibold text-gray-800">Things to do in {cityName} →</span>
                  <span className="text-xs text-gray-500 mt-1">Complete travel guide for {cityName}</span>
                </Link>
                
                {hasItinerary(citySlug) ? (
                  <Link
                    to={`/itineraries/${citySlug}`}
                    className="flex flex-col bg-white rounded-2xl p-5 hover:shadow-md transition-shadow border border-transparent hover:border-[#5b7c67]/30"
                  >
                    <span className="text-[10px] uppercase tracking-widest text-[#5b7c67] mb-2 font-semibold">Itinerary</span>
                    <span className="text-sm font-semibold text-gray-800">{cityName} travel plan →</span>
                    <span className="text-xs text-gray-500 mt-1">Day-by-day {cityName} itinerary</span>
                  </Link>
                ) : (
                  <div className="flex flex-col bg-gray-50/50 rounded-2xl p-5 border border-gray-100 opacity-60 cursor-not-allowed">
                    <span className="text-[10px] uppercase tracking-widest text-gray-500 mb-2 font-semibold">Itinerary</span>
                    <span className="text-sm font-semibold text-gray-500">{cityName} travel plan</span>
                    <span className="text-[10px] inline-block w-max mt-1 bg-gray-200 text-gray-600 px-2 py-0.5 rounded uppercase font-bold tracking-wider">Coming soon</span>
                  </div>
                )}

                {relatedBlogs.map((b) => (
                  <Link
                    key={b._id}
                    to={`/blogs/${b.slug}`}
                    className="flex flex-col bg-white rounded-2xl p-5 hover:shadow-md transition-shadow border border-transparent hover:border-[#5b7c67]/30"
                  >
                    <span className="text-[10px] uppercase tracking-widest text-[#5b7c67] mb-2 font-semibold">Article</span>
                    <span className="text-sm font-semibold text-gray-800">{b.title} →</span>
                    <span className="text-xs text-gray-500 mt-1">{b.readTime || "5 min read"}</span>
                  </Link>
                ))}
              </div>
            </div>
          </aside>

          {/* ── FOOTER NAV ── */}
          <div className="mt-16 pt-8 border-t border-gray-200">
            <Link
              to="/blogs"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#5b6f00]/10 text-[#5b6f00] text-sm font-semibold hover:bg-[#5b6f00]/20 transition-colors duration-200"
            >
              ← Back to all articles
            </Link>
          </div>

        </article>
      </section>
    </>
  );
};

export default BlogDetail;
